/**
 * Shared Navigation Constants
 * Used across MainLayout menu and breadcrumb navigation to ensure consistency.
 *
 * Main nav shape = Board E (holistic plan S0.2):
 * 主資料 Master Data │ 銷售 Sales │ 採購 Procurement │ 訂單控管中心 Order
 * Control Center │ 船務 Shipping │ 會計 Finance │ 報表 Reports │ 系統 System.
 * Groups/items are flag-gated and HIDDEN when empty (hide-don't-disable).
 * Vocabulary: OFC terms only — see `src/constants/ofcVocabulary.ts`.
 */

import type { MenuProps } from 'antd';
import type { TFunction } from 'i18next';
import type { FeatureFlag } from '@/config/featureFlags';

type NavHandler = (path: string) => void;
type NavItems = NonNullable<MenuProps['items']>;

/**
 * Organization menu items - shared between top nav and breadcrumbs
 */
export const getOrganizationMenuItems = (
  navigate: (path: string) => void,
  t: TFunction,
  confirmBeforeNavigate?: (callback: () => void) => void,
  isEnabled?: (flag: FeatureFlag) => boolean
): MenuProps['items'] => {
  const handleNavigation = (path: string) => {
    if (confirmBeforeNavigate) {
      confirmBeforeNavigate(() => navigate(path));
    } else {
      navigate(path);
    }
  };

  const items: NavItems = [];

  if (!isEnabled || isEnabled('mdm.vendors')) {
    items.push({
      key: '/auth/vendors',
      label: t('common:menu.vendor'),
      onClick: () => handleNavigation('/auth/vendors'),
    });
  }
  if (!isEnabled || isEnabled('mdm.customers')) {
    items.push({
      key: '/auth/customers',
      label: t('common:menu.customer'),
      onClick: () => handleNavigation('/auth/customers'),
    });
  }
  if (!isEnabled || isEnabled('mdm.categories')) {
    items.push({
      key: '/auth/categories',
      label: t('common:menu.category'),
      onClick: () => handleNavigation('/auth/categories'),
    });
  }

  return items;
};

/** Build top-level module menus filtered by feature flags (Board E shape) */
export function buildMainMenuItems(opts: {
  navigate: (path: string) => void;
  t: TFunction;
  confirmBeforeNavigate: (callback: () => void) => void;
  isEnabled: (flag: FeatureFlag) => boolean;
}): MenuProps['items'] {
  const { navigate, t, confirmBeforeNavigate, isEnabled } = opts;
  const handleNavigation: NavHandler = (path: string) => {
    confirmBeforeNavigate(() => navigate(path));
  };

  const navItem = (path: string, label: string) => ({
    key: path,
    label,
    onClick: () => handleNavigation(path),
  });

  const items: NavItems = [];

  // 主資料 Master Data — flat items per Board E
  const masterChildren: NavItems = [];
  if (isEnabled('mdm.vendors')) {
    masterChildren.push(navItem('/auth/vendors', t('common:menu.vendor')));
  }
  if (isEnabled('mdm.customers')) {
    masterChildren.push(navItem('/auth/customers', t('common:menu.customer')));
  }
  if (isEnabled('mdm.categories')) {
    masterChildren.push(navItem('/auth/categories', t('common:menu.category')));
  }
  if (isEnabled('mdm.products')) {
    masterChildren.push(navItem('/auth/products', t('common:menu.products')));
    // S7.2 — SCN 共同規格主檔 (Spec Master): rides the SAME mdm.products flag
    // (FD10, no new flags) and the /auth/products permission prefix
    // (products.manage — Frame 274「SCN 要開權限」, build-default Q-O1).
    // Hidden with the flag off (hide-don't-disable). Label lives in the new
    // `scn` namespace (cross-namespace colon syntax, the reports precedent).
    masterChildren.push(navItem('/auth/products/scn', t('scn:menu.specMaster')));
  }
  if (isEnabled('mdm.productTaxonomy')) {
    masterChildren.push(navItem('/auth/product-taxonomy', t('common:menu.productTaxonomy')));
  }
  masterChildren.push(navItem('/auth/contacts', t('common:menu.contact')));
  if (isEnabled('governance.core')) {
    masterChildren.push(navItem('/auth/governance', t('common:menu.governance')));
  }
  if (masterChildren.length) {
    items.push({
      key: 'master-data',
      label: t('common:menu.masterData'),
      children: masterChildren,
    });
  }

  // 銷售 Sales
  const salesChildren: NavItems = [];
  if (isEnabled('sales.orders')) {
    salesChildren.push(navItem('/auth/sales-orders', t('common:menu.salesOrders')));
  }
  if (isEnabled('sales.claims')) {
    salesChildren.push(navItem('/auth/claims', t('common:menu.claims')));
  }
  if (salesChildren.length) {
    items.push({ key: 'sales', label: t('common:menu.sales'), children: salesChildren });
  }

  // 採購 Procurement — 進貨 Goods Receipt lives HERE (Board E pink placement)
  const procurementChildren: NavItems = [];
  if (isEnabled('procurement.po')) {
    procurementChildren.push(navItem('/auth/purchase-orders', t('common:menu.purchaseOrders')));
  }
  if (isEnabled('fulfillment.receive')) {
    procurementChildren.push(navItem('/auth/goods-receipts', t('common:menu.goodsReceipts')));
  }
  if (procurementChildren.length) {
    items.push({
      key: 'procurement',
      label: t('common:menu.procurement'),
      children: procurementChildren,
    });
  }

  // 訂單控管中心 Order Control Center — top-level, ONE module
  if (isEnabled('occ.board')) {
    items.push(navItem('/auth/occ', t('common:menu.occ')));
  }

  // 船務 Shipping
  const shippingChildren: NavItems = [];
  if (isEnabled('shipping.notice')) {
    shippingChildren.push(navItem('/auth/shipping-notices', t('common:menu.shippingNotices')));
  }
  if (isEnabled('shipping.batch')) {
    shippingChildren.push(navItem('/auth/shipments', t('common:menu.shipments')));
  }
  if (shippingChildren.length) {
    items.push({ key: 'shipping', label: t('common:menu.shipping'), children: shippingChildren });
  }

  // 會計 Finance — Board E order: Invoices · AR · AP · GL.
  // S7.1: Invoices (customer 依已裝量開立 + vendor manual intake) rides the
  // AR/AP flags — a dedicated 'finance.invoices' flag needs featureFlags.ts
  // (outside the S7.1 file set; follow-up recorded).
  const financeChildren: NavItems = [];
  if (isEnabled('finance.ar') || isEnabled('finance.ap')) {
    financeChildren.push(navItem('/auth/finance/invoices', t('common:menu.invoices')));
  }
  if (isEnabled('finance.ar')) {
    financeChildren.push(navItem('/auth/finance/ar', t('common:menu.ar')));
  }
  if (isEnabled('finance.ap')) {
    financeChildren.push(navItem('/auth/finance/ap', t('common:menu.ap')));
  }
  if (isEnabled('finance.glStatements')) {
    financeChildren.push(navItem('/auth/finance/gl', t('common:menu.gl')));
  }
  if (financeChildren.length) {
    items.push({ key: 'finance', label: t('common:menu.finance'), children: financeChildren });
  }

  // 報表 Reports — the five pink reports (S11.1). Route-derived tab shell
  // (/auth/reports/:report); each child deep-links a report so ReportsPage
  // selects the matching tab. Rides the umbrella reports.opsPack flag.
  const reportChildren: NavItems = [];
  if (isEnabled('reports.opsPack')) {
    reportChildren.push(
      navItem('/auth/reports/order-margin', t('reports:reports.tabs.orderMargin'))
    );
    reportChildren.push(navItem('/auth/reports/back-order', t('reports:reports.tabs.backOrder')));
    reportChildren.push(navItem('/auth/reports/unreceived', t('reports:reports.tabs.unreceived')));
    reportChildren.push(
      navItem('/auth/reports/receivables', t('reports:reports.tabs.receivables'))
    );
    reportChildren.push(navItem('/auth/reports/payables', t('reports:reports.tabs.payables')));
  }
  if (reportChildren.length) {
    items.push({ key: 'reports', label: t('common:menu.reports'), children: reportChildren });
  }

  // 系統 System — Board E slots: 匯率 FX · 操作紀錄 Audit · 權限 Permissions ·
  // 定價設定 · 分類層級設定. FX/Audit/Category-levels arrive with their waves
  // (W9/W10); until then only existing routes render here. The 權限 slot is
  // filled by the rbacV2 deep links below (Board E order: before 定價設定).
  const systemChildren: NavItems = [];
  // rbacV2 System deep links (S4.2) — 組織 org tree + 角色階層 role hierarchy
  // jump into the route-derived governance tabs (/auth/governance/:tab; labels
  // reuse `governance:tabs.*` so nav and tab always read identically). Gated on
  // BOTH flags: `governance.core` mounts the /auth/governance routes (their
  // FeatureGate in App.tsx), `governance.rbacV2` registers the hierarchy tab
  // and the tree/matrix upgrades — with either flag off the destination is
  // dark, so the entries are HIDDEN (hide-don't-disable) and the nav renders
  // byte-identical to pre-rbacV2. 等級 Levels deliberately has NO third entry:
  // levels are managed inside 角色階層 via its 管理層級 modal (S2.3, modal not
  // route) — a separate nav entry would duplicate the same destination
  // (ux-lens duplicated-signal rule).
  if (isEnabled('governance.core') && isEnabled('governance.rbacV2')) {
    systemChildren.push(
      navItem('/auth/governance/organisations', t('governance:tabs.organisations'))
    );
    systemChildren.push(navItem('/auth/governance/hierarchy', t('governance:tabs.hierarchy')));
  }
  if (isEnabled('engines.pricing')) {
    // S1.4 — points at the real S9.1 Price Setup surface (route-derived tabs
    // under /auth/pricing/setup/:tab). The legacy /auth/engines/pricing URL
    // stays as a redirect in App.tsx for old links, but the nav names the
    // destination directly so selection highlighting can follow the URL.
    systemChildren.push(navItem('/auth/pricing/setup', t('common:menu.pricingSettings')));
  }
  if (systemChildren.length) {
    items.push({ key: 'system', label: t('common:menu.system'), children: systemChildren });
  }

  return items;
}
